import React, { useEffect, useMemo, useState } from 'react';
import { Button, message, Spin, Tabs, Tooltip } from 'antd';
import { TCarousel, type TMedicationSetting, type TObjectAction } from 'types/helloKlarity';
import EditMedication from '../EditMedication';
import { deleteMedicationById, exchangeMedicationInSpecialty, getKlarityMedicationListByPageId, updateMedicationSpecialtyExchange } from 'api/operation';
// import { getMedicationList } from 'api/operation/pageManagement';
import s from './s.module.less';
// dnd-kit 相关
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, horizontalListSortingStrategy, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from './components/SortableItem'; // 你需要新建该组件
import { useParams } from 'react-router-dom';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import useGetKlaritySpecialtiesByPageId from 'hooks/useGetKlaritySpecialtiesByPageId';
import { IMedication, ISpecialty } from 'types/operation';
import { CSS } from '@dnd-kit/utilities';
import { MAX_MEDICATION_LENGTH } from 'constants/medications';
import SpecialtyCard from './components/SpecialtyCard';
import EditSpecialtyModal from './components/EditSpecialtyModal';
import cx from 'classnames';
import { LeftOutlined } from '@ant-design/icons';
import DeleteMedicationModal from './components/DeleteMedicationModal';

interface DraggableTabPaneProps extends React.HTMLAttributes<HTMLDivElement> {
    'data-node-key': string;
}

type IProps = {
    pageId?: string;
    isLoading: boolean;
    onSuccess: (obj: TObjectAction) => void;
    onPreview: (list: ISpecialty[]) => void;
    // onExchange: (obj: TObjectAction) => void;
    // onRefresh: () => void;
};

type TTabs = {
    key: string;
    label: React.ReactNode
};

type TMedication = {
    id: number;
    imageUrl: string;
    medicationPageUrl: string;
    pageId: number;
    seq: number;
    specialtyId: number;
    specialtyName: string;
    specialtyPriority: number | null;
    subtext: string;
    tag: string;
    title?: string;
};

const DraggableTabNode = ({ className, ...props }: DraggableTabPaneProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
        id: (props as any)['data-node-key'],
    });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Transform.toString(transform && { ...transform, scaleX: 1 }),
        transition,
        cursor: 'grab',
        // border: '2px solid #00816B',
        // background: '#F3FAF8',
    };

    return React.cloneElement(
        props.children as React.ReactElement,
        {
            ref: setNodeRef,
            style,
            ...attributes,
            ...listeners,
        },
    );
};

const MedicationManagement: React.FC<IProps> = ({
    pageId,
    isLoading,
    onPreview,
    onSuccess,
    // onExchange,
    // onRefresh,
}) => {
    const [activeIndex, setActiveIndex] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<ISpecialty>();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [item, setItem] = useState<IMedication>();
    const [medications, setMedications] = useState<IMedication[]>([]);
    // const [specialties, setSpecialties] = useState<Specialty[]>([]);
    const [showDetail, setShowDetail] = useState<{
        status: 'A' | 'D';
        index: number;
    } | null>(null);
    const { type } = useParams();
    const [draggableItems, setDraggableItems] = useState<TTabs[]>([] as TTabs[]);
    const [specialties, specialtiesLoading, refetchSpecialties] = useGetKlaritySpecialtiesByPageId(pageId!);
    const [medicationsLoading, setMedicationsLoading] = useState(false);
    const [undraggableItems, setUndraggableItems] = useState<TTabs[]>(
        [] as TTabs[],
    );
    const [editSpecialtyModalVisible, setEditSpecialtyModalVisible] = useState(false);
    const [editingSpecialty, setEditingSpecialty] = useState<ISpecialty>();
    const [deleteMedicationModalVisible, setDeleteMedicationModalVisible] = useState(false);
    const [deletingMedicationInfo, setDeletingMedicationInfo] = useState<IMedication>();
    const [exchangeSpecialtyLoading, setExchangeSpecialtyLoading] = useState(false);
    const [deleteMedicationLoading, setDeleteMedicationLoading] = useState(false);
    const [medicationType, setMedicationType] = useState<'edit' | 'add'>('edit');
    const [exchangeMedicationLoading, setExchangeMedicationLoading] = useState(false);

    const sensor = useSensor(PointerSensor, {
        activationConstraint: { distance: 10 },
    });

    const activeSpecialties = useMemo(() => {
        const filteredSpecialties = specialties.sort((a, b) => (a.specialtyPriority || 0) - (b.specialtyPriority || 0)).filter((e) => e.status === 'A') as unknown as ISpecialty[];
        // 为每个specialty添加medications字段
        return filteredSpecialties.map((specialty) => ({
            ...specialty,
            medications: medications.filter((med) => med.specialtyId === specialty.id),
        }));
    }, [specialties, medications]);

    const inactiveSpecialties = useMemo(() => {
        const filteredSpecialties = specialties.sort((a, b) => (a.specialtyPriority || 0) - (b.specialtyPriority || 0)).filter((e) => e.status === 'D') as unknown as ISpecialty[];
        // 为每个specialty添加medications字段
        return filteredSpecialties.map((specialty) => ({
            ...specialty,
            medications: medications.filter((med) => med.specialtyId === specialty.id),
        }));
    }, [specialties, medications]);

    const currentIndex = useMemo(() => {
        const list = showDetail?.status === 'A' ? activeSpecialties : inactiveSpecialties;
        const index = list.findIndex((e) => e.id === selectedItem?.id);
        return index > -1 ? index : 0;
    }, [showDetail, activeSpecialties, inactiveSpecialties, selectedItem]);

    useEffect(() => {
        if (showDetail) {
            const list = showDetail.status === 'A' ? activeSpecialties : inactiveSpecialties;
            const currentItem = list[currentIndex];
            setSelectedItem(currentItem);
            setActiveIndex(currentItem ? `tab-${currentItem.id}` : '');
            onPreview(list);
        }
    }, [showDetail, activeSpecialties, inactiveSpecialties, currentIndex]);

    const refetchData = () => {
        // 使用promise all，等待2个api都返回后，进行判断
        Promise.all([refetchSpecialties(), getList()]).then(() => {
            const newSelectedItem = activeSpecialties.find((e) => e.id === selectedItem?.id);
            if (!newSelectedItem) {
                setSelectedItem(undefined);
            } else {
                setSelectedItem(newSelectedItem);
            }
        });
    };

    const handleSuccess = (obj: TObjectAction) => {
        setIsOpen(false);
        setItem(undefined);
        onSuccess(obj);
        refetchData();
    };

    const getList = async () => {
        if (pageId) {
            try {
                setMedicationsLoading(true);
                const res = await getKlarityMedicationListByPageId(pageId);
                if (res && !res.error) {
                    setMedications(res.data.data || []);
                } else {
                    message.error(res?.error);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setMedicationsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (pageId) {
            getList();
        }
    }, [pageId]);

    const handlePreview = (tabKey: string) => {
        const list = showDetail?.status === 'A' ? activeSpecialties : inactiveSpecialties;
        const index = Number(tabKey.substring(4));
        const specialty = list.find((listItem) => listItem.id === index) || list[0];
        setActiveIndex(`tab-${specialty.id}`);
        setSelectedItem(specialty);
        onPreview([specialty]);
    };

    const onDragStart = ({ active }: DragStartEvent) => {
        handlePreview(`${active.id}`);
    };

    const handleTabClick = (key: string) => {
        handlePreview(key);
    };

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setDraggableItems((prev) => {
                const currentId = prev.findIndex((i) => i.key === active.id);
                const overId = prev.findIndex((i) => i.key === over?.id);
                return arrayMove(prev, currentId, overId);
            });

            const fromId = `${active.id}`.substring(4);
            const toId = `${over?.id}`.substring(4);

            try {
                setExchangeSpecialtyLoading(true);
                const res = await updateMedicationSpecialtyExchange(fromId, toId);
                if (res && !res.error) {
                    refetchData();
                } else {
                    message.error(res?.error);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setExchangeSpecialtyLoading(false);
            }
        }
    };

    // dnd-kit sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    // 横向拖拽排序 Active specialties
    // const handleActiveSpecialtyDragEnd = (event: any) => {
    //     const { active, over } = event;
    //     if (!over || active.id === over.id) return;
    //     const oldIndex = activeSpecialties.findIndex((e) => e.id === active.id);
    //     const newIndex = activeSpecialties.findIndex((e) => e.id === over.id);
    //     if (oldIndex === -1 || newIndex === -1) return;
    //     const newList = arrayMove(activeSpecialties, oldIndex, newIndex);
    //     // 立即保存到后端（mock）
    //     const newSpecialties = [
    //         ...newList,
    //         ...specialties.filter((s) => s.status !== 'A') as ISpecialty[],
    //     ];
    //     // setSpecialties(newSpecialties);
    //     // TODO: 调用保存接口
    //     setSelectedSpecialtyIdx(newIndex);
    // };

    // 横向滚动容器ref
    const scrollRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (activeSpecialties.length > 0) {
            const arr = activeSpecialties.map((e) => {
                return {
                    key: `tab-${e.id}`,
                    label: (
                        <div className={s.infoWrap}>
                            <div className={s.infoBox}>
                                <h4 className={s.carouselTitle}>{e.specialtyName}</h4>
                                {/* <p className={s.carouselText}>{e.medications.length} medications</p> */}
                            </div>
                        </div>
                    ),
                };
            });

            /*
            * The first item cannot be dragged when type is CONDITION or STATE
            */

            if (type !== 'HOME') {
                const firstItem = arr.slice(0, 1);
                const resetOfArray = arr.slice(1);
                setUndraggableItems(firstItem);
                setDraggableItems(resetOfArray);
            } else {
                setDraggableItems(arr);
            }
        }
    }, [activeSpecialties]);

    // 鼠标滚轮横向滚动
    React.useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            if (e.deltaY !== 0) {
                el.scrollLeft += e.deltaY;
                e.preventDefault();
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const handlePrev = () => {
        if (currentIndex - 1 >= 0) {
            const specItem = inactiveSpecialties[currentIndex - 1];
            handlePreview(`tab-${specItem.id}`);
        }
    };

    const handleNext = () => {
        if (currentIndex + 1 < inactiveSpecialties.length) {
            const specItem = inactiveSpecialties[currentIndex + 1];
            handlePreview(`tab-${specItem.id}`);
        }
    };

    const renderDeactiveCarousel = () => {
        return (
            <div className={s.swiperHeader}>
                <div className={cx(s.swiperBtn, s.prev)} onClick={() => handlePrev()} />
                <div className={s.subTitle}>
                    Inactive carousel {currentIndex + 1} / {inactiveSpecialties.length || 0}
                </div>
                <div className={cx(s.swiperBtn, s.next)} onClick={() => handleNext()} />
            </div>
        );
    };

    const resetAllInfo = () => {
        setShowDetail(null);
        setSelectedItem(undefined);
        setActiveIndex('');
    };

    const onMedicationDragEnd = async ({ active, over }: DragEndEvent) => {
        console.info('onMedicationDragEnd', active, over);
        if (active.id !== over?.id) {
            const fromId = active.id;
            const toId = over?.id;

            if (fromId && toId) {
                try {
                    setExchangeMedicationLoading(true);
                    const res = await exchangeMedicationInSpecialty(fromId.toString(), toId.toString());
                    if (res && !res.error) {
                        refetchData();
                    } else {
                        message.error(res?.error);
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    setExchangeMedicationLoading(false);
                }
            }
        }
    };

    const handleDeleteMedication = async () => {
        if (deletingMedicationInfo?.id) {
            try {
                setDeleteMedicationLoading(true);
                const res = await deleteMedicationById(deletingMedicationInfo?.id);
                if (res && !res.error) {
                    setDeleteMedicationModalVisible(false);
                    setDeletingMedicationInfo(undefined);
                    refetchData();
                } else {
                    message.error(res?.error);
                }
            } catch (e) {
                console.error(e);
                message.error('Failed to delete medication');
            } finally {
                setDeleteMedicationLoading(false);
            }
        } else {
            message.error('Medication ID is required');
        }
    };

    const renderActiveCarousel = () => {
        return (
            <>
                <div className={s.sortList}>
                    {undraggableItems.map((e, index) => {
                        return (
                            <Tooltip
                                title="You cannot change the order of carousel 1"
                                placement="bottom"
                            >
                                <div
                                    key={index}
                                    className={`${s.sortItem} ${
                                        e.key === activeIndex ? s.active : ''
                                    } ${activeSpecialties.length <= 2 ? s.halfWidth : s.oneThirdWidth}`}
                                    onClick={() => {
                                        handlePreview(e.key);
                                    }}
                                >
                                    {e.label}
                                </div>
                            </Tooltip>
                        );
                    })}
                    <Tabs
                        items={draggableItems}
                        onTabClick={handleTabClick}
                        animated={false}
                        // tabPosition="top"
                        activeKey={activeIndex}
                        renderTabBar={(tabBarProps, DefaultTabBar) => (
                            <DndContext
                                sensors={[sensor]}
                                onDragEnd={onDragEnd}
                                onDragStart={onDragStart}
                            >
                                <SortableContext
                                    items={draggableItems.map((i) => i.key)}
                                    strategy={horizontalListSortingStrategy}
                                >
                                    <DefaultTabBar {...tabBarProps}>
                                        {(node) => (
                                            <DraggableTabNode {...node.props} key={node.key}>
                                                {node}
                                            </DraggableTabNode>
                                        )}
                                    </DefaultTabBar>
                                </SortableContext>
                            </DndContext>
                        )}
                    />
                </div>
                {activeSpecialties.length >= MAX_MEDICATION_LENGTH && (
                    <div className={s.tips}>
                        A maximum of {MAX_MEDICATION_LENGTH} medications can be added to one specialty. Remove existing medications to add new ones.
                    </div>
                )}
                {(!selectedItem?.medications || selectedItem.medications.length === 0) && (
                    <div className={s.errorTips}>
                        This specialty will not be displayed on the Helloklarity home page if medication is not added.
                    </div>
                )}
            </>
        );
    };

    // 详情页内容
    const renderDetail = () => {
        if (!showDetail) return null;
        return (
            <div className={s.detailWrap}>
                <div className={s.back} onClick={resetAllInfo}>
                    <LeftOutlined />
                    Back
                </div>

                <div className={s.detailHeader}>
                    <span className={s.specialtyTitle}>{showDetail.status === 'A' ? 'Active specialties' : 'Inactive specialties'}</span>
                    <Button
                        type="primary"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => {
                            setMedicationType('add');
                            setIsOpen(true);
                        }}
                        disabled={selectedItem?.medications && selectedItem?.medications?.length >= MAX_MEDICATION_LENGTH}
                    >
                        Add new medication
                    </Button>
                </div>
                {
                    showDetail.status === 'A' && renderActiveCarousel()
                }
                {
                    showDetail.status === 'D' && inactiveSpecialties.length > 0 && renderDeactiveCarousel()
                }
                {
                    selectedItem && (
                        <SpecialtyCard
                            index={currentIndex}
                            onEdit={() => {
                                setEditingSpecialty(selectedItem);
                                setEditSpecialtyModalVisible(true);
                            }}
                            specialty={selectedItem}
                        />
                    )
                }
                <div className={s.medicationList}>
                    {selectedItem?.medications?.length === 0 && (
                        <div className={s.empty}>No medications</div>
                    )}
                    <DndContext sensors={sensors} onDragEnd={onMedicationDragEnd}>
                        <SortableContext
                            items={selectedItem?.medications?.map((med) => med.id) || []}
                            strategy={verticalListSortingStrategy}
                        >

                            {selectedItem?.medications?.sort((a, b) => (a.seq || 0) - (b.seq || 0)).map((med, index) => (
                                <SortableItem
                                    specialties={specialties}
                                    key={med.id}
                                    id={med.id}
                                    med={med}
                                    index={index}
                                    onEdit={() => {
                                        setItem(med);
                                        setMedicationType('edit');
                                        setIsOpen(true);
                                    }}
                                    onRemove={() => {
                                        setDeletingMedicationInfo(med);
                                        setDeleteMedicationModalVisible(true);
                                    }}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
        );
        // ...原有inactive specialties详情页...
        // ... existing code ...
    };

    return (
        <div className={s.wrap}>
            <Spin spinning={isLoading || specialtiesLoading || medicationsLoading || exchangeSpecialtyLoading || exchangeMedicationLoading}>
                {!showDetail && (
                    <>
                        <div className={s.header}>
                            <div className={s.title}>Medication specialties</div>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setEditingSpecialty(undefined);
                                    setEditSpecialtyModalVisible(true);
                                }}
                            >
                                Add new specialty
                            </Button>
                        </div>
                        <ul className={s.specialtyList}>
                            <li
                                className={s.specialtyItem}
                                onClick={() => {
                                    setShowDetail({ status: 'A', index: 0 });
                                }}
                            >
                                Active specialties ({activeSpecialties.length})
                            </li>
                            <li
                                className={s.specialtyItem}
                                onClick={() => {
                                    setShowDetail({ status: 'D', index: 0 });
                                }}
                            >
                                Inactive specialties ({inactiveSpecialties.length})
                            </li>
                        </ul>
                    </>
                )}
                {showDetail && renderDetail()}
            </Spin>
            {isOpen && (
                <EditMedication
                    currentIndex={currentIndex}
                    item={item}
                    list={medications as any}
                    currentSpecialty={selectedItem}
                    specialties={specialties}
                    onCancel={() => setIsOpen(false)}
                    onSuccess={handleSuccess}
                    type={medicationType}
                />
            )}
            <DeleteMedicationModal
                loading={deleteMedicationLoading}
                open={deleteMedicationModalVisible}
                onCancel={() => setDeleteMedicationModalVisible(false)}
                onConfirm={handleDeleteMedication}
                specialtyName={selectedItem?.specialtyName}
                medication={deletingMedicationInfo}
            />
            <EditSpecialtyModal
                open={editSpecialtyModalVisible}
                specialty={editingSpecialty}
                pageId={pageId!}
                onSuccess={() => {
                    setEditSpecialtyModalVisible(false);
                    setEditingSpecialty(undefined);
                    refetchData();
                }}
                onCancel={() => {
                    setEditSpecialtyModalVisible(false);
                    setEditingSpecialty(undefined);
                }}
            />
        </div>
    );
};

export default MedicationManagement;
